<template>
    <b-container fluid class="mb-5">
        <b-media vertical-align="center">
            <b-row>
                <b-col cols="12" lg="10" xl="4">
                    <indicator-progress :target="target" :nameLink="true" />
                </b-col>
                <b-col cols="12" sm="2" md="2" lg="2" xl="2">
                    <div class="text-center text-muted mt-2 mt-xl-0">
                        <c-weight :weight="target.weight" :calibrated-weight="target.calibratedWeight" />
                    </div>
                </b-col>
                <b-col cols="12" sm="10" md="10" lg="12" xl="2">
                    <div class="text-center text-muted mt-2 mt-xl-0">
                        <c-achieved :achieved="target.achieved" :progressBarColor="target.range" />
                    </div>
                </b-col>
                <b-col class="p-0">
                    <div class="mt-2 mb-0 mt-xl-0">
                        <checkin-button
                            :target="goal"
                            :isAllowedToDoCheckin="canDoCheckIn(target)"
                            @checkin="openTheModalCheckIn(target.owner.id, target.id)"
                        />
                    </div>
                </b-col>
                <b-col class="p-0">
                    <div :id="disableViewEvaluations" class="text-center mt-2 mb-0 mt-xl-0 p-0">
                        <b-button
                            id="btn-target-view-evaluations"
                            variant="link"
                            :disabled="!canViewEvaluations"
                            @click="showEvaluations = !showEvaluations"
                        >
                            <p class="mb-0" style="font-size: 15px">
                                <icon-evaluation />
                            </p>
                            <p class="mb-0">
                                <small>{{ $t('values.evaluations') }}</small>
                            </p>
                            <b-tooltip :target="disableViewEvaluations" id="" v-if="!canViewEvaluations">
                                {{ viewEvaluationTooltipMessage }}
                            </b-tooltip>
                        </b-button>
                    </div>
                </b-col>
                <b-col class="p-0">
                    <div class="text-center mt-2 mt-xl-0 p-0">
                        <edit-target-button
                            :target="target"
                            @reload="$emit('reload')"
                            :currentEventPermissions="currentEventPermissions"
                        />
                    </div>
                </b-col>
                <b-col class="p-0">
                    <div class="text-center mt-2 mt-xl-0 p-0">
                        <delete-target-button
                            :target="target"
                            @reload="$emit('reload')"
                            :currentEventPermissions="currentEventPermissions"
                        />
                    </div>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b-collapse id="evaluations-colapse" :visible="showEvaluations">
                        <b-row>
                            <b-col cols="12" :sm="evaluationsCols" v-if="showSelfEvaluation">
                                <div class="text-center text-muted mt-1">
                                    <c-evaluation
                                        :evaluationType="'selfEvaluate'"
                                        :evaluationValue="
                                            target.ownerEvaluation ? target.ownerEvaluation.weightedEvaluation : null
                                        "
                                        :evaluationColor="target.colors ? target.colors.ownerEvaluation : null"
                                        :target="target"
                                        :isAllowedToEvaluate="false"
                                        :showEvaluation="showSelfEvaluation"
                                        :viewLink="false"
                                        @updated="$emit('updated')"
                                    />
                                </div>
                            </b-col>

                            <b-col cols="12" :sm="evaluationsCols" v-if="showManagerEvaluation">
                                <div class="text-center text-muted mt-1">
                                    <c-evaluation
                                        :evaluationType="'managerEvaluate'"
                                        :evaluationValue="
                                            target.managerEvaluation
                                                ? target.managerEvaluation.weightedEvaluation
                                                : null
                                        "
                                        :evaluationColor="target.colors ? target.colors.managerEvaluation : null"
                                        :target="target"
                                        :viewLink="false"
                                        :showEvaluation="showManagerEvaluation"
                                        :isAllowedToEvaluate="false"
                                        @updated="$emit('updated')"
                                    />
                                </div>
                            </b-col>

                            <b-col cols="12" :sm="evaluationsCols" v-if="showCommitteeEvaluation">
                                <div class="text-center text-muted mt-1">
                                    <c-evaluation
                                        :evaluationType="'committeeEvaluation'"
                                        :evaluationValue="
                                            target.committeeEvaluation
                                                ? target.committeeEvaluation.weightedEvaluation
                                                : null
                                        "
                                        :evaluationColor="target.colors ? target.colors.committeeEvaluation : null"
                                        :target="target"
                                        :viewLink="false"
                                        :isAllowedToEvaluate="false"
                                        :showEvaluation="showCommitteeEvaluation"
                                        @updated="$emit('updated')"
                                    />
                                </div>
                            </b-col>
                        </b-row>
                    </b-collapse>
                </b-col>
            </b-row>
        </b-media>
    </b-container>
</template>

<script>
import { formatNumber, formatPercentage, decimalFormat } from '@/lib/utils'
import { userPermissionsMixin } from '@/mixins'
import { IconEvaluation } from '@/views/icons'

import {
    IndicatorProgress,
    CheckinButton,
    EditButton,
    EditTargetButton,
    DeleteTargetButton,
} from '@/views/target/components/view/indicator-header'

import {
    Weight as CWeight,
    Achieved as CAchieved,
    Evaluation as CEvaluation,
} from '@/views/target/targets/children/components/target/target-header/components'

import { swalMixin, stringsMixin } from '@/mixins'

export default {
    name: 'Target',
    mixins: [swalMixin, stringsMixin, userPermissionsMixin],
    components: {
        IndicatorProgress,
        CWeight,
        CAchieved,
        CEvaluation,
        CheckinButton,
        EditButton,
        EditTargetButton,
        DeleteTargetButton,
        IconEvaluation,
    },
    props: {
        goal: {
            type: Object,
            required: true,
        },
        isUserAllowedToEdit: {
            type: Boolean,
            required: true,
        },
        type: {
            type: String,
            required: false,
        },
    },
    data() {
        return {
            showEvaluations: false,
            target: {
                owner: {
                    picture: null,
                    name: '',
                },
                ownerEvaluation: null,
                colors: {
                    ownerEvaluation: null,
                },
            },
            currentEventPermissions: {
                personal: {
                    checkin: null,
                },
                unit: {
                    checkin: null,
                },
                department: {
                    checkin: null,
                },
            },
            evaluationType: '',
            showView: false,
        }
    },
    computed: {
        disableViewEvaluations() {
            return 'disable-view-evaluations-' + this.goal.id
        },
        currentUser() {
            return this.$store.state.currentUser.user
        },
        showSelfEvaluation() {
            if (!this.target.level) {
                return false
            }
            return this.currentEventPermissions[this.target.level].allow_view_self_evaluation
        },
        showManagerEvaluation() {
            if (!this.target.level) {
                return false
            }
            const canView = this.currentEventPermissions[this.target.level].allow_view_manager_evaluation
            const isOwnerManager = this.currentUser.id === this.target.owner.manager_id
            return canView || isOwnerManager
        },
        showCommitteeEvaluation() {
            if (!this.target.level) {
                return false
            }
            return this.currentEventPermissions[this.target.level].allow_view_committee_evaluation
        },
        evaluationsCols() {
            let cols = 0
            if (this.showSelfEvaluation) {
                cols++
            }
            if (this.showManagerEvaluation) {
                cols++
            }
            if (this.showCommitteeEvaluation) {
                cols++
            }
            return (12 / cols).toString()
        },
        isTargetTab() {
            return this.type == 'targets'
        },
        canViewEvaluations() {
            return (
                this.currentEventPermissions[this.target.level].allow_view_committee_evaluation ||
                this.currentEventPermissions[this.target.level].allow_view_self_evaluation ||
                this.currentEventPermissions[this.target.level].allow_view_manager_evaluation
            )
        },
        viewEvaluationTooltipMessage() {
            return (
                this.$t('profile.targetsTable.userWithoutPermissionViewEvaluations', {
                    stageTitle: this.$store.state.profile.user.stages.current.title,
                }) + this.viewStageCriteriaMessage
            )
        },
        viewStageCriteriaMessage() {
            const firstEntryCriteria = 0
            if (!this.hasEntryCriteria) {
                return ''
            }
            const criterionIdentifier =
                this.$store.state.profile.user.stages.current.entryCriteria[firstEntryCriteria].criterion.identifier
            return this.$t(`stages.eventHasCriteriaMessages.${criterionIdentifier}`)
        },
        hasEntryCriteria() {
            return Object.keys(this.$store.state.profile.user.stages.current.entryCriteria).length
        },
    },
    created() {
        this.target = this.objectKeysToCamelCase(this.goal)
        this.currentEventPermissions = this.$store.state.filters.currentEventPermissions.goals
    },
    methods: {
        formatNumber(number) {
            return formatNumber(number)
        },
        formatPercentage(number) {
            return formatPercentage(number)
        },
        decimalFormat(number) {
            return decimalFormat(number)
        },
        getColor(key) {
            if (this.target[key] === null) {
                return null
            }
            if (this.target[key] !== null && this.target.colors[key] === null) {
                return null
            }
            return this.target.colors[key]
        },
        openTheModalCheckIn(userId, targetId) {
            const id = this.type != 'targets' ? this.currentUser.id : userId
            this.$router.push({
                path: `/v2/profile/${id}/goal/${targetId}/checkin?type=${this.type}`,
            })
        },
        canDoCheckIn(target) {
            const checkInPermission = this.getCheckinPermission(target)
            const targetIsNotCompleted = !(target.status === 'completed')
            return checkInPermission && targetIsNotCompleted
        },
        getCheckinPermission(target) {
            const permissionKey = this.type == 'focal-points' ? 'checkin_as_focal_point' : 'checkin'
            return this.currentEventPermissions[target.level] !== undefined
                ? this.currentEventPermissions[target.level][permissionKey]
                : false
        },
        didCurrentUserContractThisIndicator() {
            return this.target.indicatorUsersIds.includes(this.currentUser.id)
        },
        isTargetUnavailableToEdit() {
            return (this.targetIsCompleted() || this.isPeriodFinished()) && !this.userHasRolePermission()
        },
        userHasRolePermission() {
            const availableRoles = ['god', 'administrator', 'local_administrator', 'is_target_master']
            const rolesPermission = this.userHasPermission(this.currentUser, availableRoles, [])
            return rolesPermission
        },
        targetIsCompleted() {
            return this.target.status === 'completed'
        },
        isPeriodFinished() {
            if (!this.target.period) return true

            return this.target.period.status === 'finished'
        },
        viewEvaluation(event) {
            this.evaluationType = event
            this.showView = !this.showView
        },
        getTarget() {
            this.$emit('reload')
            this.showView = false
            this.evaluationType = ''
        },
    },
}
</script>
<style lang="scss" scoped>
.btn-link {
    padding: 0;
}
#btn-target-view-evaluations.disabled {
    color: #6e6e6e;
    cursor: not-allowed;
    opacity: 1;
    text-decoration: none;
}
#btn-target-view-evaluations svg {
    fill: #009bda;
}
#btn-target-view-evaluations.disabled svg {
    fill: #6e6e6e;
}
</style>
